import { useEffect } from 'react';
import clsx from 'clsx';
import { useStyles } from './SessionNavigator.styles';
import { Box, Collapse } from '@mui/material';
import { Icons } from '../../../../icons';
import { colors } from '../../../../colors';
import { useSelector } from 'react-redux';
import { selectAssignedEvaluationState } from '../../../../../redux/modules/conversation/selectors';
import { Sections, subsections } from '../sessions.constants';
import { MemberActions } from '../../../molecules/member-actions';

const SessionNavigator = ({
  isFirstAppointment,
  selectedSection,
  setSelectedSection,
  sessionEnded,
  selectedEvaluationId,
  setSelectedEvaluationId,
  currentBlock,
  setCurrentBlock,
  currentEvaluation,
  setCurrentEvaluation,
  listHelper,
  isPrescriber,
  onMemberActionClick,
  treatmentPlan,
  setEvaluationClicked,
  expanded,
  toggleExpand,
  setExpanded,
  setScrollToTop,
  getPreviousResponsesForDCT,
}) => {
  const classes = useStyles({ expanded });
  const { evaluations } = useSelector(selectAssignedEvaluationState);

  useEffect(() => {
    if (sessionEnded) {
      setExpanded(true);
    }
  }, [sessionEnded]);

  const cleanupSelections = () => {
    listHelper.setHead(null);
    setSelectedEvaluationId(null);
    setCurrentEvaluation(null);
    setCurrentBlock(null);
  };

  const isSectionCompleted = currentSection => {
    const sectionOrder = [Sections.WELCOME, Sections.EVALUATIONS];
    if (isPrescriber) {
      sectionOrder.push(Sections.MEDICATIONS);
    }
    sectionOrder.push(Sections.OPEN_SESSION, Sections.CLOSE_SESSION);
    const currentIndex = sectionOrder.indexOf(currentSection);
    const selectedIndex = sectionOrder.indexOf(selectedSection);
    return selectedIndex > currentIndex;
  };

  return (
    <Box className={classes.wrapper}>
      {/* Session navigator content */}
      <Box className={classes.collapseArea}>
        <Box className={classes.collapseButton} onClick={toggleExpand}>
          <Icons glyph="collapsable-outlined" />
        </Box>
      </Box>
      <Box className={classes.stepsContainer}>
        {/* Welcome Section */}
        <Box
          className={classes.mainSection}
          onClick={() => {
            cleanupSelections();
            setSelectedSection(Sections.WELCOME);
            setSelectedEvaluationId(subsections.IMPORTANT_INFORMATION);
          }}
        >
          {expanded ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                transition: 'transform 0.3s',
                transform: selectedSection === Sections.WELCOME ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <Icons
                glyph="chevron-down"
                color={colors.neutral500}
              />
            </Box>
          ) : (
            <span
              className={clsx(classes.sectionCount, classes.sectionCountFirst, {
                [classes.sectionSelected]: selectedSection === Sections.WELCOME,
                [classes.sectionCompleted]: isSectionCompleted(Sections.WELCOME),
              })}
            >
              1
            </span>
          )}
          {expanded && (
            <span
              className={clsx(classes.sectionTitle)}
            >
              Welcome
            </span>
          )}
        </Box>
        <Collapse in={selectedSection === Sections.WELCOME}>
          {expanded ? (
            <p
              className={clsx(classes.subsection, {
                [classes.subsectionSelected]: selectedEvaluationId === subsections.IMPORTANT_INFORMATION,
              })}
              onClick={() => {
                cleanupSelections();
                setSelectedSection(Sections.WELCOME);
                setSelectedEvaluationId(subsections.IMPORTANT_INFORMATION);
              }}
            >
              Important Information
            </p>
          ) : (
            <Box
              className={clsx(classes.collapsedDot, {
                [classes.collapsedDotActive]: selectedEvaluationId === subsections.IMPORTANT_INFORMATION,
              })}
              onClick={() => {
                cleanupSelections();
                setSelectedSection(Sections.WELCOME);
                setSelectedEvaluationId(subsections.IMPORTANT_INFORMATION);
              }}
            />
          )}
          {isFirstAppointment &&
            (expanded ? (
              <p
                className={clsx(classes.subsection, {
                  [classes.subsectionSelected]: selectedEvaluationId === subsections.DOUBLE_CHECK_IDS,
                })}
                onClick={() => {
                  cleanupSelections();
                  setSelectedSection(Sections.WELCOME);
                  setSelectedEvaluationId(subsections.DOUBLE_CHECK_IDS);
                }}
              >
                Double Check IDs
              </p>
            ) : (
              <Box
                className={clsx(classes.collapsedDot, {
                  [classes.collapsedDotActive]: selectedEvaluationId === subsections.DOUBLE_CHECK_IDS,
                })}
                onClick={() => {
                  cleanupSelections();
                  setSelectedSection(Sections.WELCOME);
                  setSelectedEvaluationId(subsections.DOUBLE_CHECK_IDS);
                }}
              />
            ))}
          {treatmentPlan &&
            (expanded ? (
              <p
                className={clsx(classes.subsection, {
                  [classes.subsectionSelected]: selectedEvaluationId === subsections.TREATMENT_PLAN,
                })}
                onClick={() => {
                  cleanupSelections();
                  setSelectedSection(Sections.WELCOME);
                  setSelectedEvaluationId(subsections.TREATMENT_PLAN);
                }}
              >
                Treatment Plan
              </p>
            ) : (
              <Box
                className={clsx(classes.collapsedDot, {
                  [classes.collapsedDotActive]: selectedEvaluationId === subsections.TREATMENT_PLAN,
                })}
                onClick={() => {
                  cleanupSelections();
                  setSelectedSection(Sections.WELCOME);
                  setSelectedEvaluationId(subsections.TREATMENT_PLAN);
                }}
              />
            ))}
        </Collapse>
        {/* Collect Data Section */}
        <Box
          className={clsx(classes.mainSection, {
            [classes.sectionHighlight]: selectedSection === Sections.EVALUATIONS && !currentEvaluation && expanded,
          })}
          onClick={() => {
            cleanupSelections();
            setSelectedSection(Sections.EVALUATIONS);
          }}
        >
          {expanded ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                transition: 'transform 0.3s',
                transform: selectedSection === Sections.EVALUATIONS ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <Icons
                glyph="chevron-down"
                color={selectedSection === Sections.EVALUATIONS ? colors.primary500 : colors.neutral500}
              />
            </Box>
          ) : (
            <span
              className={clsx(classes.sectionCount, {
                [classes.sectionSelected]: selectedSection === Sections.EVALUATIONS,
                [classes.sectionCompleted]: isSectionCompleted(Sections.EVALUATIONS),
              })}
            >
              2
            </span>
          )}
          {expanded && (
            <span
              className={clsx(classes.sectionTitle, {
                [classes.sectionTitleSelected]:
                  selectedSection === Sections.EVALUATIONS,
              })}
            >
              Collect Data
            </span>
          )}
        </Box>
        {evaluations?.filter(ev => ev.status !== 'REJECTED').length > 0 && (
          <Collapse in={selectedSection === Sections.EVALUATIONS}>
            {evaluations
              .filter(ev => ev.status !== 'REJECTED' && !ev.isRecommended)
              .map((evaluation, evalIndex, evalArray) => {
                const isEvaluationSelected = currentEvaluation && currentEvaluation.id === evaluation.id;
                // For EXERCISE type, keep parent highlighted when viewing any step
                const isExerciseWithBlock = evaluation.type === 'EXERCISE' && isEvaluationSelected && currentBlock;
                return (
                  <Box className={classes.evaluationContainer} key={evaluation.id}>
                    {expanded ? (
                      <Box
                        className={clsx(classes.subsection, {
                          [classes.subsectionSelected]: (isEvaluationSelected && !currentBlock) || isExerciseWithBlock,
                          [classes.subsectionTextHighlight]: isEvaluationSelected && currentBlock && evaluation.type !== 'EXERCISE',
                        })}
                        onClick={() => {
                          if (isEvaluationSelected) {
                            // Collapse if already selected
                            cleanupSelections();
                          } else {
                            // Expand if not selected
                            cleanupSelections();
                            setSelectedSection(Sections.EVALUATIONS);
                            setCurrentEvaluation(evaluation);
                            setEvaluationClicked(true);
                            setScrollToTop(true);
                          }
                        }}
                      >
                        {evaluation.name}
                      </Box>
                    ) : (
                      <Box
                        className={clsx(classes.collapsedDot, {
                          [classes.collapsedDotActive]: isEvaluationSelected,
                        })}
                        onClick={() => {
                          if (isEvaluationSelected) {
                            // Collapse if already selected
                            cleanupSelections();
                          } else {
                            // Expand if not selected
                            cleanupSelections();
                            setSelectedSection(Sections.EVALUATIONS);
                            setCurrentEvaluation(evaluation);
                            setEvaluationClicked(true);
                            setScrollToTop(true);
                          }
                        }}
                      />
                    )}
                    <Collapse in={isEvaluationSelected}>
                      {/* For EVALUATION type: show dcts */}
                      {isEvaluationSelected && evaluation.type === 'EVALUATION' && listHelper.getDcts && listHelper.getDcts().length > 0 && listHelper.getDcts().map((dct, dctIndex, dctArray) => {
                        return expanded ? (
                          <p
                            className={clsx(classes.dctName, {
                              [classes.subsectionSelected]: currentBlock && currentBlock.dctId === dct.dctId,
                            })}
                            key={dct.dctId}
                            onClick={() => {
                              getPreviousResponsesForDCT(dct);
                              setCurrentBlock(dct);
                              setScrollToTop(true);
                            }}
                          >
                            – {dct.text}
                          </p>
                        ) : (
                          <Box
                            key={dct.dctId}
                            className={clsx(classes.collapsedDot, {
                              [classes.collapsedDotActive]: currentBlock && currentBlock.dctId === dct.dctId,
                            })}
                            onClick={() => {
                              getPreviousResponsesForDCT(dct);
                              setCurrentBlock(dct);
                              setScrollToTop(true);
                            }}
                          />
                        );
                      })}
                    </Collapse>
                  </Box>
                );
              })}
          </Collapse>
        )}
        {/* Medications Section */}
        {isPrescriber && (
          <Box
            className={classes.mainSection}
            onClick={() => {
              cleanupSelections();
              setSelectedSection(Sections.MEDICATIONS);
            }}
          >
            {expanded ? (
              <Icons glyph="medication-2" color={selectedSection === Sections.MEDICATIONS ? colors.primary500 : colors.neutral500} />
            ) : (
              <span
                className={clsx(classes.sectionCount, {
                  [classes.sectionSelected]: selectedSection === Sections.MEDICATIONS,
                  [classes.sectionCompleted]: isSectionCompleted(Sections.MEDICATIONS),
                })}
              >
              3
            </span>
            )}
            {expanded && (
              <span
                className={clsx(classes.sectionTitle, {
                  [classes.sectionTitleSelected]: selectedSection === Sections.MEDICATIONS,
                })}
              >
              Medications
            </span>
            )}
          </Box>
        )}
        {/* Open Session Section */}
        <Box
          className={classes.mainSection}
          onClick={() => {
            cleanupSelections();
            setSelectedSection(Sections.OPEN_SESSION);
          }}
        >
          {expanded ? (
            <Icons glyph="chat-square" color={selectedSection === Sections.OPEN_SESSION ? colors.primary500 : colors.neutral500} />
          ) : (
            <span
              className={clsx(classes.sectionCount, {
                [classes.sectionSelected]: selectedSection === Sections.OPEN_SESSION,
                [classes.sectionCompleted]: isSectionCompleted(Sections.OPEN_SESSION),
                [classes.sectionCountLast]: !sessionEnded,
              })}
            >
              {isPrescriber ? '4' : '3'}
            </span>
          )}
          {expanded && (
            <span
              className={clsx(classes.sectionTitle, {
                [classes.sectionTitleSelected]: selectedSection === Sections.OPEN_SESSION,
              })}
            >
              Open session
            </span>
          )}
        </Box>
        {/* Close Session Section - only shown when session ended */}
        {sessionEnded && (
          <Box
            className={classes.mainSection}
            onClick={() => {
              cleanupSelections();
              setSelectedSection(Sections.CLOSE_SESSION);
            }}
          >
            {expanded ? (
              <Icons
                glyph="close-session"
                color={selectedSection === Sections.CLOSE_SESSION ? colors.primary500 : colors.neutral500}
              />
            ) : (
              <span
                className={clsx(classes.sectionCount, classes.sectionCountLast, {
                  [classes.sectionSelected]: selectedSection === Sections.CLOSE_SESSION,
                })}
              >
                4
              </span>
            )}
            {expanded && (
              <span
                className={clsx(classes.sectionTitle, {
                  [classes.sectionTitleSelected]: selectedSection === Sections.CLOSE_SESSION,
                })}
              >
                Close the Session
              </span>
            )}
          </Box>
        )}
      </Box>
      <MemberActions onMemberActionClick={onMemberActionClick} expanded={expanded} />
    </Box>
  );
};
export { SessionNavigator };
